import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { authAxios } from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

export default function AdminSeasonDraft() {
  const { user, currentSeason, API } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [draftConfig, setDraftConfig] = useState(null);
  const [activePlayers, setActivePlayers] = useState([]);
  const [contestantCount, setContestantCount] = useState(0);
  
  // Draft settings state
  const [draftTiming, setDraftTiming] = useState('after_episode_1');
  const [draftOrderType, setDraftOrderType] = useState('snake');
  const [draftMode, setDraftMode] = useState('live'); // live or auto
  const [doubleDraftEnabled, setDoubleDraftEnabled] = useState(false);
  const [doubleDraftRounds, setDoubleDraftRounds] = useState('');
  const [tribeBalanceEnabled, setTribeBalanceEnabled] = useState(false);
  const [pickTimerEnabled, setPickTimerEnabled] = useState(false);
  const [pickTimerSeconds, setPickTimerSeconds] = useState('60');
  const [customPlayerOrder, setCustomPlayerOrder] = useState([]);
  const [tribes, setTribes] = useState([]);
  const [contestantsWithTribes, setContestantsWithTribes] = useState(0);
  
  // Auto-draft status
  const [rankingsStatus, setRankingsStatus] = useState(null);
  
  // UI state
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showAutoDraftDialog, setShowAutoDraftDialog] = useState(false);
  const [showRedoDialog, setShowRedoDialog] = useState(false);
  const [starting, setStarting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [redoing, setRedoing] = useState(false);

  useEffect(() => {
    if (currentSeason) {
      loadDraftData();
    }
  }, [currentSeason]);

  const loadDraftData = async () => {
    try {
      setLoading(true);
      const [draftRes, tribesRes, contestantsRes] = await Promise.all([
        authAxios.get(`${API}/seasons/${currentSeason.id}/draft`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/tribes`),
        authAxios.get(`${API}/seasons/${currentSeason.id}/contestants`)
      ]);
      
      const response = draftRes;
      setTribes(tribesRes.data || []);
      
      // Count contestants that have tribes assigned
      const withTribes = (contestantsRes.data || []).filter(c => c.tribe_id).length;
      setContestantsWithTribes(withTribes);
      
      setActivePlayers(response.data.active_players);
      setContestantCount(response.data.contestant_count);
      
      if (response.data.draft) {
        const draft = response.data.draft;
        setDraftConfig(draft);
        setDraftTiming(draft.draft_timing);
        setDraftOrderType(draft.draft_order_type);
        setDraftMode(draft.draft_mode || 'live');
        setDoubleDraftEnabled(draft.double_draft_enabled);
        setDoubleDraftRounds(draft.double_draft_rounds?.toString() || '');
        setTribeBalanceEnabled(draft.tribe_balance_enabled || false);
        setPickTimerEnabled(draft.pick_timer_enabled);
        setPickTimerSeconds(draft.pick_timer_seconds?.toString() || '60');
        
        if (draft.draft_order && draft.draft_order.length > 0) {
          setCustomPlayerOrder(draft.draft_order[0] || []);
        }
        
        // Load rankings status if in auto mode
        if (draft.draft_mode === 'auto') {
          loadRankingsStatus();
        }
      } else {
        // Set default double draft based on player count and timing
        const playerCount = response.data.active_players.length;
        if (draftTiming === 'before_episode_1' && playerCount >= 7) {
          setDoubleDraftEnabled(true);
        } else if (draftTiming === 'after_episode_1' && playerCount >= 6) {
          setDoubleDraftEnabled(true);
        }
      }
    } catch (error) {
      console.error('Failed to load draft data:', error);
      toast.error('Failed to load draft data');
    } finally {
      setLoading(false);
    }
  };

  const loadRankingsStatus = async () => {
    try {
      const response = await authAxios.get(`${API}/seasons/${currentSeason.id}/draft/rankings/status`);
      setRankingsStatus(response.data);
    } catch (error) {
      console.error('Failed to load rankings status:', error);
    }
  };

  const saveDraftSettings = async () => {
    try {
      setSaving(true);
      
      await authAxios.post(
        `${API}/seasons/${currentSeason.id}/draft`,
        {
          draft_timing: draftTiming,
          draft_order_type: draftOrderType,
          draft_mode: draftMode,
          double_draft_enabled: doubleDraftEnabled,
          double_draft_rounds: doubleDraftEnabled && doubleDraftRounds ? parseInt(doubleDraftRounds) : null,
          tribe_balance_enabled: tribeBalanceEnabled,
          contestant_count: contestantCount,
          pick_timer_enabled: draftMode === 'live' && pickTimerEnabled,
          pick_timer_seconds: draftMode === 'live' && pickTimerEnabled ? parseInt(pickTimerSeconds) : null,
          custom_player_order: draftOrderType === 'custom' ? customPlayerOrder : null
        }
      );
      
      toast.success('Draft settings saved!');
      loadDraftData();
    } catch (error) {
      console.error('Failed to save draft settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save draft settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExecuteAutoDraft = async () => {
    try {
      setStarting(true);
      
      await authAxios.post(`${API}/seasons/${currentSeason.id}/draft/auto-execute`);
      
      toast.success('Auto-draft completed! Players can now view the draft replay.');
      setShowAutoDraftDialog(false);
      loadDraftData();
    } catch (error) {
      console.error('Failed to execute auto-draft:', error);
      toast.error(error.response?.data?.detail || 'Failed to execute auto-draft');
    } finally {
      setStarting(false);
    }
  };

  const handleStartDraft = async () => {
    try {
      setStarting(true);
      
      await authAxios.post(
        `${API}/seasons/${currentSeason.id}/draft/start`,
        {}
      );
      
      toast.success('Draft started successfully!');
      setShowStartDialog(false);
      loadDraftData();
    } catch (error) {
      console.error('Failed to start draft:', error);
      toast.error(error.response?.data?.detail || 'Failed to start draft');
    } finally {
      setStarting(false);
    }
  };

  const handleRedoDraft = async () => {
    try {
      setRedoing(true);
      
      const response = await authAxios.post(`${API}/seasons/${currentSeason.id}/draft/redo`);
      
      toast.success(`Draft reset! ${response.data.picks_deleted} picks and ${response.data.rosters_deleted} rosters cleared.`);
      setShowRedoDialog(false);
      loadDraftData();
      
      // Reload the page to update season status
      window.location.reload();
    } catch (error) {
      console.error('Failed to redo draft:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset draft');
    } finally {
      setRedoing(false);
    }
  };

  const calculateDraftOutput = () => {
    const playerCount = activePlayers.length;
    let contestants = contestantCount;
    
    // If drafting after episode 1, reduce contestant count by 1 (one voted off)
    if (draftTiming === 'after_episode_1') {
      contestants = contestants - 1;
    }
    
    if (playerCount === 0) return { 
      rounds: 0, 
      picksPerPlayer: 0, 
      totalPicks: 0, 
      availableContestants: contestants,
      undraftedContestants: 0
    };
    
    let picksPerPlayer, totalPicks, undraftedContestants;
    
    if (doubleDraftEnabled) {
      // In double draft, multiple players can pick same contestant
      if (doubleDraftRounds && parseInt(doubleDraftRounds) > 0) {
        // Use custom rounds setting
        picksPerPlayer = parseInt(doubleDraftRounds);
      } else {
        // Default to 3-4 picks per player
        picksPerPlayer = Math.min(4, Math.max(3, Math.floor(contestants / playerCount)));
      }
      totalPicks = picksPerPlayer * playerCount;
      undraftedContestants = 0; // In double draft, all can be picked multiple times
    } else {
      // Single draft: ALL players get SAME number of picks
      // Use floor division - some contestants may not be drafted
      picksPerPlayer = Math.floor(contestants / playerCount);
      totalPicks = picksPerPlayer * playerCount;
      undraftedContestants = contestants - totalPicks;
    }
    
    const rounds = picksPerPlayer;
    
    return { 
      rounds, 
      picksPerPlayer, 
      totalPicks, 
      availableContestants: contestants,
      undraftedContestants
    };
  };

  const draftOutput = calculateDraftOutput();

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You need admin privileges to access this page.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading draft settings...</p>
        </div>
      </Layout>
    );
  }

  const canStartDraft = currentSeason?.status === 'setup' && currentSeason?.draft_status === 'not_started';
  const draftInProgress = currentSeason?.draft_status === 'in_progress';
  const draftCompleted = currentSeason?.draft_status === 'completed';
  const wrongStatus = currentSeason?.status !== 'setup';

  return (
    <Layout>
      <div className="max-w-6xl mx-auto container-mobile">
        <div className="spacing-section">
          <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Season Draft
          </h1>
          <p className="text-responsive text-slate-600">Season {currentSeason?.number} Draft Setup</p>
        </div>

        {wrongStatus && !draftInProgress && !draftCompleted && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 text-amber-800 bg-amber-50 p-4 rounded-lg">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="font-semibold mb-1">Draft Cannot Be Started</p>
                  <p className="text-sm">Season is not in Setup stage. Current status: {currentSeason?.status}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {draftCompleted && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 text-emerald-800 bg-emerald-50 p-4 rounded-lg">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="font-semibold mb-1">Draft Completed</p>
                  <p className="text-sm">The draft has been completed. All players have made their selections.</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => setShowRedoDialog(true)}
                  data-testid="redo-draft-btn-completed"
                >
                  Redo Draft
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {draftInProgress && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 text-emerald-800 bg-emerald-50 p-4 rounded-lg">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="font-semibold mb-1">Draft In Progress</p>
                  <p className="text-sm">The draft is currently active. Players can now make their picks.</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => setShowRedoDialog(true)}
                  data-testid="redo-draft-btn-in-progress"
                >
                  Redo Draft
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-6">
          {/* Start Draft Button */}
          {canStartDraft && (
            <Card>
              <CardContent className="p-6">
                {draftMode === 'live' ? (
                  <>
                    <Button 
                      size="lg" 
                      className="w-full"
                      onClick={() => setShowStartDialog(true)}
                      disabled={!draftConfig || activePlayers.length === 0}
                    >
                      Start Live Draft
                    </Button>
                    {!draftConfig && (
                      <p className="text-sm text-slate-600 mt-2 text-center">
                        Please save draft settings before starting
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <Button 
                      size="lg" 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => setShowAutoDraftDialog(true)}
                      disabled={!draftConfig || activePlayers.length === 0 || draftConfig?.draft_mode !== 'auto'}
                    >
                      Run Auto-Draft
                    </Button>
                    {!draftConfig && (
                      <p className="text-sm text-slate-600 mt-2 text-center">
                        Please save draft settings before starting
                      </p>
                    )}
                    {draftConfig && draftConfig?.draft_mode !== 'auto' && (
                      <p className="text-sm text-amber-600 mt-2 text-center">
                        Save settings first to enable auto-draft mode
                      </p>
                    )}
                    {draftConfig?.draft_mode === 'auto' && (
                      <p className="text-sm text-purple-600 mt-2 text-center">
                        Players should rank their contestants before you run the auto-draft
                      </p>
                    )}
                  </>
                )}
                {activePlayers.length === 0 && (
                  <p className="text-sm text-red-600 mt-2 text-center">
                    No active players found for this season
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Draft Output */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-slate-800 mb-6">Draft Output</h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-600">Active Players</div>
                    <div className="text-2xl font-bold text-slate-800">{activePlayers.length}</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-600">Total Contestants</div>
                    <div className="text-2xl font-bold text-slate-800">{contestantCount}</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-600">Available for Draft</div>
                    <div className="text-2xl font-bold text-slate-800">
                      {draftOutput.availableContestants}
                      {draftTiming === 'after_episode_1' && (
                        <span className="text-sm text-slate-600 ml-1">(-1)</span>
                      )}
                    </div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-600">Rounds</div>
                    <div className="text-2xl font-bold text-slate-800">{draftOutput.rounds}</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-600">Picks per Player</div>
                    <div className="text-2xl font-bold text-slate-800">{draftOutput.picksPerPlayer}</div>
                    <div className="text-xs text-slate-500 mt-1">Same for all players</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-600">Total Picks</div>
                    <div className="text-2xl font-bold text-slate-800">{draftOutput.totalPicks}</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-sm text-slate-600">Undrafted</div>
                    <div className="text-2xl font-bold text-slate-800">{draftOutput.undraftedContestants}</div>
                    {!doubleDraftEnabled && draftOutput.undraftedContestants > 0 && (
                      <div className="text-xs text-amber-600 mt-1">Won't be drafted</div>
                    )}
                  </div>
                </div>

                {draftTiming === 'after_episode_1' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ After Episode 1 Logic:</strong> One contestant will be voted off, so available contestants reduced by 1 for the draft ({contestantCount} → {draftOutput.availableContestants}).
                    </p>
                  </div>
                )}

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Draft Style:</strong> {doubleDraftEnabled ? 'Double Draft' : 'Single Ownership'}
                  </p>
                  <p className="text-sm text-blue-800 mt-1">
                    <strong>Order:</strong> {draftOrderType === 'snake' ? 'Snake (alternating)' : draftOrderType === 'random' ? 'Random' : 'Custom'}
                  </p>
                  {doubleDraftEnabled && (
                    <p className="text-sm text-blue-800 mt-1">
                      Multiple players can select the same contestant. Each player will draft {draftOutput.picksPerPlayer} contestant(s).
                    </p>
                  )}
                  {!doubleDraftEnabled && (
                    <p className="text-sm text-blue-800 mt-1">
                      📊 All players will draft exactly {draftOutput.picksPerPlayer} contestant(s) each. 
                      {draftOutput.undraftedContestants > 0 && (
                        <span> {draftOutput.undraftedContestants} contestant(s) will not be drafted to ensure equal picks for all players.</span>
                      )}
                      {draftOutput.undraftedContestants === 0 && (
                        <span> All {draftOutput.availableContestants} contestants will be drafted.</span>
                      )}
                    </p>
                  )}
                </div>

                {/* Active Players List */}
                <div>
                  <h3 className="font-semibold mb-2">Active Players for Draft</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activePlayers.map(player => (
                          <tr key={player.id} className="border-t">
                            <td className="px-3 py-2 font-medium">{player.name}</td>
                            <td className="px-3 py-2 text-slate-600">{player.email}</td>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                Active
                              </span>
                            </td>
                          </tr>
                        ))}
                        {activePlayers.length === 0 && (
                          <tr>
                            <td colSpan={3} className="px-3 py-8 text-center text-slate-600">
                              No active players found for this season
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Draft Settings */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-slate-800 mb-6">Draft Settings</h2>
              
              <div className="space-y-6">
                {/* Draft Timing */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Draft Timing</Label>
                  <div className="space-y-2">
                    <div 
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        draftTiming === 'before_episode_1'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDraftTiming('before_episode_1')}
                    >
                      <div className="font-semibold">Before Episode 1</div>
                      <div className="text-sm text-slate-600">All contestants available. Allow double drafting with 7+ active players.</div>
                    </div>
                    <div 
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        draftTiming === 'after_episode_1'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDraftTiming('after_episode_1')}
                    >
                      <div className="font-semibold">After Episode 1 (Default)</div>
                      <div className="text-sm text-slate-600">One contestant voted off (available count -1). Allow double drafting with 6+ active players.</div>
                    </div>
                  </div>
                </div>

                {/* Draft Order */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Draft Order</Label>
                  <div className="space-y-2">
                    <div 
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        draftOrderType === 'snake'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDraftOrderType('snake')}
                    >
                      <div className="font-semibold">Snake (Default)</div>
                      <div className="text-sm text-slate-600">First round order is randomized, then alternates. Example: Round 1: A→B→C→D (random), Round 2: D→C→B→A</div>
                    </div>
                    <div 
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        draftOrderType === 'random'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDraftOrderType('random')}
                    >
                      <div className="font-semibold">Random</div>
                      <div className="text-sm text-slate-600">Randomly order players (no consecutive picks)</div>
                    </div>
                    <div 
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        draftOrderType === 'custom'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDraftOrderType('custom')}
                    >
                      <div className="font-semibold">Custom</div>
                      <div className="text-sm text-slate-600">Manually arrange player order</div>
                    </div>
                  </div>
                </div>

                {/* Other Draft Options */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Draft Mode</Label>
                  <div className="space-y-2">
                    <div 
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        draftMode === 'live'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDraftMode('live')}
                    >
                      <div className="font-semibold">Live Draft (Default)</div>
                      <div className="text-sm text-slate-600">Players take turns picking contestants in real-time. Best when everyone can be online together.</div>
                    </div>
                    <div 
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        draftMode === 'auto'
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setDraftMode('auto')}
                    >
                      <div className="font-semibold flex items-center gap-2">
                        Auto-Draft (Ranked Choice)
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Remote Friendly</span>
                      </div>
                      <div className="text-sm text-slate-600">
                        Each player ranks contestants 1→N ahead of time. System runs the draft automatically using those rankings.
                        Perfect for larger leagues or when scheduling is difficult.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Auto-Draft Status (only show if auto mode selected and saved) */}
                {draftMode === 'auto' && draftConfig?.draft_mode === 'auto' && rankingsStatus && (
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-purple-800">Player Rankings Status</h4>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={loadRankingsStatus}
                        className="text-purple-700 border-purple-300"
                      >
                        Refresh
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {rankingsStatus.players?.map(player => (
                        <div key={player.user_id} className="flex items-center justify-between text-sm">
                          <span>{player.name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            player.submitted_at 
                              ? 'bg-green-100 text-green-700' 
                              : player.has_rankings 
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-slate-100 text-slate-600'
                          }`}>
                            {player.submitted_at 
                              ? '✓ Submitted' 
                              : player.has_rankings 
                                ? `${player.rankings_count} ranked`
                                : 'Not started'}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-purple-200 flex items-center justify-between">
                      <span className="text-sm text-purple-700">
                        {rankingsStatus.submitted_count} / {rankingsStatus.total_players} submitted
                      </span>
                      {rankingsStatus.all_submitted && (
                        <span className="text-sm text-green-600 font-medium">✓ Ready to run!</span>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-base font-semibold mb-3 block">Other Options</Label>
                  <div className="space-y-3">
                    {/* Double Draft Toggle */}
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                      <input
                        type="checkbox"
                        id="doubleDraft"
                        checked={doubleDraftEnabled}
                        onChange={(e) => setDoubleDraftEnabled(e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="doubleDraft" className="cursor-pointer flex-1">
                        <div className="font-semibold">Enable Double Drafting</div>
                        <div className="text-sm text-slate-600">
                          Multiple players can select the same contestant
                          {draftTiming === 'before_episode_1' && activePlayers.length < 7 && (
                            <span className="text-amber-600"> (Recommended with 7+ players)</span>
                          )}
                          {draftTiming === 'after_episode_1' && activePlayers.length < 6 && (
                            <span className="text-amber-600"> (Recommended with 6+ players)</span>
                          )}
                        </div>
                      </label>
                    </div>

                    {/* Double Draft Rounds (shown when double draft is enabled) */}
                    {doubleDraftEnabled && (
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
                        <div className="font-semibold text-amber-800">Number of Rounds</div>
                        <div className="flex items-center gap-3">
                          <Input
                            type="number"
                            min="1"
                            max="10"
                            value={doubleDraftRounds}
                            onChange={(e) => setDoubleDraftRounds(e.target.value)}
                            placeholder="Auto"
                            className="max-w-[100px]"
                          />
                          <span className="text-sm text-slate-600">rounds (leave empty for auto: 3-4)</span>
                        </div>
                        <p className="text-xs text-amber-700">
                          Each player will get {doubleDraftRounds || 'auto-calculated'} picks. 
                          With {activePlayers.length} players, that's {doubleDraftRounds ? (parseInt(doubleDraftRounds) * activePlayers.length) : '~' + (Math.min(4, Math.max(3, Math.floor(contestantCount / activePlayers.length))) * activePlayers.length)} total picks.
                        </p>
                      </div>
                    )}

                    {/* Tribe Balance Toggle (Auto-Draft only) */}
                    {draftMode === 'auto' && (
                      <div className={`p-4 rounded-lg space-y-3 ${contestantsWithTribes === contestantCount && tribes.length >= 3 ? 'bg-slate-50' : 'bg-slate-100 opacity-60'}`}>
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="tribeBalance"
                            checked={tribeBalanceEnabled}
                            onChange={(e) => setTribeBalanceEnabled(e.target.checked)}
                            disabled={contestantsWithTribes < contestantCount || tribes.length < 3}
                            className="rounded"
                          />
                          <label htmlFor="tribeBalance" className="cursor-pointer flex-1">
                            <div className="font-semibold">Enable Tribe Balance</div>
                            <div className="text-sm text-slate-600">
                              Prevents "disaster tribes" - max 2 contestants per tribe, all tribes represented
                            </div>
                          </label>
                        </div>
                        {(contestantsWithTribes < contestantCount || tribes.length < 3) && (
                          <p className="text-xs text-amber-700">
                            {tribes.length < 3 
                              ? `Requires at least 3 tribes (currently ${tribes.length})`
                              : `All contestants must have tribes assigned (${contestantsWithTribes}/${contestantCount} have tribes)`
                            }
                          </p>
                        )}
                        {tribeBalanceEnabled && contestantsWithTribes === contestantCount && tribes.length >= 3 && (
                          <p className="text-xs text-emerald-700">
                            Each player will have contestants from all {tribes.length} tribes, with no more than 2 from any single tribe.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Pick Timer */}
                    <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="pickTimer"
                          checked={pickTimerEnabled}
                          onChange={(e) => setPickTimerEnabled(e.target.checked)}
                          className="rounded"
                        />
                        <label htmlFor="pickTimer" className="cursor-pointer font-semibold">
                          Enable Pick Timer
                        </label>
                      </div>
                      <p className="text-sm text-slate-600">
                        Keeps a live draft moving: if the player on the clock runs out of time, a random
                        available castaway is drafted for them and marked "Auto" on the draft board.
                      </p>
                      {pickTimerEnabled && (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={pickTimerSeconds}
                            onChange={(e) => setPickTimerSeconds(e.target.value)}
                            placeholder="60"
                            className="max-w-[120px]"
                          />
                          <span className="text-sm text-slate-600">seconds</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <Button onClick={saveDraftSettings} disabled={saving || draftInProgress}>
                  {saving ? 'Saving...' : 'Save Draft Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Start Draft Confirmation Dialog */}
        <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg">Ready to Start the Draft?</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-3 py-2">
              {/* Warning Box */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <p className="text-amber-800 font-semibold">⚠️ Make sure all players are logged in and ready!</p>
                <p className="text-amber-700 mt-1">Only <strong>Active</strong> players can participate once the draft starts.</p>
              </div>

              {/* Best Practices - Compact */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="text-blue-800 font-semibold mb-1">💡 Tips</p>
                <ul className="text-blue-700 space-y-0.5 ml-4 list-disc text-xs">
                  <li>Coordinate a start time, especially across time zones</li>
                  <li>Players pick one at a time - if someone's slow, the draft could drag on</li>
                  <li>Use a group chat or video call to stay engaged</li>
                </ul>
              </div>

              {/* Active Players - Compact */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Active Players ({activePlayers.length})</h3>
                <div className="border rounded-lg overflow-hidden max-h-32 overflow-y-auto text-sm">
                  <table className="w-full">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-1.5 text-left text-xs">Name</th>
                        <th className="px-3 py-1.5 text-left text-xs">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activePlayers.map(player => (
                        <tr key={player.id} className="border-t">
                          <td className="px-3 py-1.5">{player.name}</td>
                          <td className="px-3 py-1.5 text-slate-600">{player.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Draft Summary - Compact */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <h4 className="font-semibold mb-1">Draft Summary</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                  <p><strong>Timing:</strong> {draftTiming === 'before_episode_1' ? 'Before Ep 1' : 'After Ep 1'}</p>
                  <p><strong>Order:</strong> {draftOrderType === 'snake' ? 'Snake' : draftOrderType === 'random' ? 'Random' : 'Custom'}</p>
                  <p><strong>Double Draft:</strong> {doubleDraftEnabled ? `Yes${doubleDraftRounds ? ` (${doubleDraftRounds}r)` : ''}` : 'No'}</p>
                  <p><strong>Contestants:</strong> {contestantCount}</p>
                  <p><strong>Rounds:</strong> {draftOutput.rounds}</p>
                  <p><strong>Picks/Player:</strong> {draftOutput.picksPerPlayer}</p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 justify-end pt-1">
                <Button variant="outline" size="sm" onClick={() => setShowStartDialog(false)}>
                  Not Yet
                </Button>
                <Button size="sm" onClick={handleStartDraft} disabled={starting} className="bg-green-600 hover:bg-green-700">
                  {starting ? 'Starting...' : "Start Draft"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Auto-Draft Confirmation Dialog */}
        <Dialog open={showAutoDraftDialog} onOpenChange={setShowAutoDraftDialog}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg">Run Auto-Draft?</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-3 py-2">
              {/* Info Box */}
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                <p className="text-purple-800 font-semibold">🤖 Ranked Choice Auto-Draft</p>
                <p className="text-purple-700 mt-1">
                  The system will simulate the draft using each player's contestant rankings.
                  On each turn, the highest-ranked available contestant will be assigned.
                </p>
              </div>

              {/* Rankings Status */}
              {rankingsStatus && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-sm font-semibold mb-2">Player Rankings Status</p>
                  <div className="space-y-1">
                    {rankingsStatus.players?.map(player => (
                      <div key={player.user_id} className="flex items-center justify-between text-xs">
                        <span>{player.name}</span>
                        <span className={`px-2 py-0.5 rounded-full ${
                          player.submitted_at 
                            ? 'bg-green-100 text-green-700' 
                            : player.has_rankings 
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                        }`}>
                          {player.submitted_at 
                            ? `✓ ${player.rankings_count} ranked` 
                            : player.has_rankings 
                              ? `${player.rankings_count} ranked (not submitted)`
                              : 'No rankings'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-600">
                    Players without rankings will receive contestants in default order (first available).
                  </div>
                </div>
              )}

              {/* Warning if not all submitted */}
              {rankingsStatus && !rankingsStatus.all_submitted && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="text-amber-800">
                    <strong>Note:</strong> Not all players have submitted their rankings. 
                    Players without rankings will be assigned contestants in the default order.
                  </p>
                </div>
              )}

              {/* Draft Summary */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <h4 className="font-semibold mb-1">Draft Summary</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                  <p><strong>Mode:</strong> Auto-Draft</p>
                  <p><strong>Order:</strong> {draftOrderType === 'snake' ? 'Snake' : draftOrderType === 'random' ? 'Random' : 'Custom'}</p>
                  <p><strong>Players:</strong> {activePlayers.length}</p>
                  <p><strong>Rounds:</strong> {draftOutput.rounds}</p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 justify-end pt-1">
                <Button variant="outline" size="sm" onClick={() => setShowAutoDraftDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleExecuteAutoDraft} 
                  disabled={starting} 
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {starting ? 'Running...' : "Run Auto-Draft"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Redo Draft Confirmation Dialog */}
        <Dialog open={showRedoDialog} onOpenChange={setShowRedoDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg">Redo Draft?</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              {/* Warning Box */}
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm">
                <p className="text-red-800 font-semibold">⚠️ This action cannot be undone!</p>
                <p className="text-red-700 mt-2">
                  Resetting the draft will:
                </p>
                <ul className="text-red-700 mt-1 ml-4 list-disc text-xs space-y-1">
                  <li>Delete all draft picks made so far</li>
                  <li>Clear all player rosters for this season</li>
                  <li>Reset the draft status to "Not Started"</li>
                  <li>Preserve the draft order for the new draft</li>
                </ul>
              </div>

              {/* Auto-Draft Rankings Preserved Notice */}
              {draftMode === 'auto' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                  <p className="text-green-800">
                    <strong>✓ Player rankings will be preserved.</strong> For auto-draft, each player's 
                    ranked contestant list remains intact. You can re-run the auto-draft without players 
                    needing to re-submit their rankings.
                  </p>
                </div>
              )}

              {/* Info Box */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="text-blue-800">
                  <strong>Use this if:</strong> Someone made a pick error, a player joined late, 
                  or you need to restart the draft for any reason.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowRedoDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleRedoDraft} 
                  disabled={redoing} 
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-redo-draft-btn"
                >
                  {redoing ? 'Resetting...' : "Yes, Reset Draft"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
